import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import { Api } from "../../api/Api";

export default function CheckoutPage() {
  const { http } = Api();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);

  /* ================= FORM STATE ================= */
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    district: "",
    address: "",
  });

  const [errors, setErrors] = useState({});

  /* ================= GET DATA FROM SESSION ================= */
  useEffect(() => {
    const sessionData = sessionStorage.getItem("checkout_product");

    if (!sessionData) {
      swal("Oops!", "No product found in checkout", "warning");
      navigate("/landing-page");
      return;
    }

    setProduct(JSON.parse(sessionData));
  }, [navigate]);

  if (!product) return null;

  /* ================= CALCULATIONS ================= */
  const subtotal = product.price * product.quantity;
  const deliveryCharge = 0;
  const total = subtotal + deliveryCharge;

  /* ================= HANDLE INPUT ================= */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    if (loading) return; // ⛔ double click prevent

    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Full name is required";
    if (!form.phone.trim()) newErrors.phone = "Phone number is required";
    if (!form.address.trim()) newErrors.address = "Full address is required";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      swal("Oops!", "Please fill all required fields", "warning");
      return;
    }

    /* ================= PAYLOAD ================= */
    const payload = {
      product_id: product.product_id || product.id,
      product_name: product.product_name,
      article_number: product.article_number || null,

      price: product.price,
      quantity: product.quantity,

      color_name: product.color?.name || null,
      size: product.size || null,

      customer_name: form.name,
      phone: form.phone,
      email: form.email || null,
      district: form.district || null,
      address: form.address,
    };

    try {
      setLoading(true); 

    /* ================= API CALL ================= */
    const res = await http.post("/orderstore", payload);

    if (res.data.success) {
        sessionStorage.setItem('order_success', 'true');
        sessionStorage.removeItem("checkout_product");
        navigate("/order-confirmed");
    } else {
      swal("Error!", "Order failed, try again", "error");
    }
    } catch (error) {
      console.error("Order submit error:", error);

      if (error.response?.data?.errors) {
        swal("Validation Error", "Please check your inputs", "warning");
      } else {
        swal("Server Error", "Something went wrong", "error");
      }
    } finally {
      setLoading(false);
    }
  };



  return (
    <>
      <section className="pt-5 mt-4"></section>

      <div className="fb-form">
        <div className="container">
          <div className="row g-4">

            {/* ================= LEFT : CUSTOMER FORM ================= */}
            <div className="col-12 col-lg-8">
              <div className="bg-white rounded-4 border p-4">
                <h4 className="fw-bold mb-4">Checkout Information</h4>

                <div className="row g-3">
                  {/* Full Name */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      className="fb-input form-control-lg"
                      placeholder="Enter your name"
                      value={form.name}
                      onChange={handleChange}
                    />
                    {errors.name && (
                      <small className="text-danger">{errors.name}</small>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Phone Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="phone"
                      className="fb-input form-control-lg"
                      placeholder="01XXXXXXXXX"
                      value={form.phone}
                      onChange={handleChange}
                    />
                    {errors.phone && (
                      <small className="text-danger">{errors.phone}</small>
                    )}
                  </div>

                  {/* Email */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Email <span className="text-muted">(Optional)</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="fb-input form-control-lg"
                      placeholder="example@gmail.com"
                      value={form.email}
                      onChange={handleChange}
                    />
                  </div>

                  {/* District */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">District <span className="text-muted">(Optional)</span></label>
                    <input
                      type="text"
                      name="district"
                      className="fb-input form-control-lg"
                      placeholder="Your District"
                      value={form.district}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Address */}
                  <div className="col-12">
                    <label className="form-label fw-semibold">
                      Your Full Address <span className="text-danger">*</span>
                    </label>
                    <textarea
                      name="address"
                      className="fb-input form-control-lg"
                      rows="3"
                      placeholder="House / Road / Area"
                      value={form.address}
                      onChange={handleChange}
                    ></textarea>
                    {errors.address && (
                      <small className="text-danger">{errors.address}</small>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    className="fb-btn-primary fb-submit-btn"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Processing...
                      </>
                    ) : (
                      "Confirm Order"
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* ================= RIGHT : ORDER SUMMARY (STICKY) ================= */}
            <div className="col-12 col-lg-4">
              <div className="order-summary-sticky">
                <div className="bg-white rounded-4 shadow-sm border p-4">
                  <h5 className="fw-bold mb-4">Order Summary</h5>

                  <div className="d-flex gap-3 align-items-center mb-4">
                    <img
                      src={product.image}
                      alt={product.product_name}
                      className="rounded-3"
                      style={{ width: "80px", height: "80px", objectFit: "cover" }}
                    />

                    <div>
                      <h6 className="fw-bold mb-1">{product.product_name}</h6>
                      {product.article_number && (
                        <div className="text-muted fw-semibold">
                          {product.article_number}
                        </div>
                      )}
                      <div className="text-muted fw-semibold">
                        Color: {product.color?.name}
                      </div>
                      <div className="text-muted fw-semibold">
                        Size: {product.size} · Qty: {product.quantity}
                      </div>
                    </div>
                  </div>

                  <div className="border-top pt-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted fw-semibold">Price</span>
                      <span className="fw-semibold">৳ {product.price}</span>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted fw-semibold">Quantity</span>
                      <span className="fw-semibold">{product.quantity}</span>
                    </div>
                  </div>

                  <div className="border-top pt-3 mt-3 d-flex justify-content-between">
                    <span className="fw-bold fs-5">Total</span>
                    <span className="fw-bold fs-5">৳ {total}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ================= STYLES ================= */}
      <style>{`
        .fb-form { background:#e6e6e6; }

        .fb-submit-btn {
          width:200px;
          padding:12px 0;
          border-radius:30px;
          display:block;
          margin:12px auto 0;
        }

        .swal-button.swal-btn-success {
          background-color: #7ac142;
          color: #fff;
          font-size: 15px;
          text-align:center;
          padding: 10px 28px;
          border-radius: 10px;
          box-shadow: 0 6px 18px rgba(122, 193, 66, 0.35);
        }

        .swal-button.swal-btn-success:hover {
          background-color: #68ab35;
        }

        .swal-footer {
          text-align: center !important;
        }


        .fb-input {
          width:100%;
          padding:12px 16px;
          border-radius:14px;
          border:1px solid #d7d7d7;
          background:#fff;
          font-size:15px;
          transition:all .25s ease;
        }

        .fb-input:focus {
          border-color:#7ac142;
          box-shadow:0 6px 18px rgba(122,193,66,.15);
          outline:none;
          transform:translateY(-2px);
        }

        .order-summary-sticky {
          position:sticky;
          top:110px;
        }

        @media (max-width:991px){
          .order-summary-sticky{ position:static; }
        }
      `}</style>
    </>
  );
}
